import { useMemo, useState, useEffect } from "react"
import { CSVLink } from "react-csv"
import { Search } from "lucide-react"
import SaveTaskModal from "./Modal/SaveTaskModal"
import { PrimaryButton, SecondaryButton, AgTableCard, Input } from "../UI";
import { useContentfulContext } from "../../contexts/ContentfulContext"
import { permission } from "../../config/permission"
import { useUserContext } from "../../contexts/UserContext"
import { blockFeaturesForCampaign, getManageAdobeTargetModalApi, blockOrUnblockGlobalFeatures, getGlobalBlocklistForCampaign } from "../../utils/APIs"
import Loader from "../UI/Loader/Loader"
import { formatDate } from "../../utils/converter"
import backwardArrow from '../../assets/icons/backward-arrow.svg';

const ManageAdobeTargetModelApi = ({ setShowBackNavigationArrow, loading: initialLoading, data, tenant_name, campaign_id }) => {
    const { checkPermission, userData } = useUserContext()
    const { content } = useContentfulContext()

    // Step management
    const [currentStep, setCurrentStep] = useState(1)

    // Step 1: Activities list
    const [activitiesGridApi, setActivitiesGridApi] = useState(null)
    const [activitiesSearchText, setActivitiesSearchText] = useState("")

    // Step 2: Features management
    const [campaignId, setCampaignId] = useState("")
    const [campaignName, setCampaignName] = useState("")
    const [isLoading, setIsLoading] = useState(false)
    const [selectedRows, setSelectedRows] = useState([])
    const [campaignData, setCampaignData] = useState(null)
    const [features, setFeatures] = useState([])
    const [error, setError] = useState(null)
    const [isBlocking, setIsBlocking] = useState(false)
    const [gridApi, setGridApi] = useState(null)
    const [searchText, setSearchText] = useState("")
    const [downloadDropdown, setDownloadDropdown] = useState(false)

    // Global features management
    const [showGlobalFeatures, setShowGlobalFeatures] = useState(false)
    const [globalFeatures, setGlobalFeatures] = useState([])
    const [globalLoading, setGlobalLoading] = useState(false)
    const [globalError, setGlobalError] = useState(null)
    const [selectedGlobalRows, setSelectedGlobalRows] = useState([])
    const [globalGridApi, setGlobalGridApi] = useState(null)
    const [globalSearchText, setGlobalSearchText] = useState("")

    // Activity columns for step 1
    const activityColumns = useMemo(
        () => [
            {
                headerName: content.LIST_MODEL_API_CONTENT.activities_table_headers.activity_id,
                field: "id",
                sortable: true,
                filter: true,
                resizable: true,
                minWidth: 150,
                cellRenderer: (params) => {
                    return (
                        <button className="text-primary hover:underline" onClick={() => { handleActivityClick(params.data) }}>
                            {params.value}
                        </button>
                    )
                },
            },
            {
                headerName: content.LIST_MODEL_API_CONTENT.activities_table_headers.activity_name,
                field: "name",
                sortable: true,
                filter: true,
                resizable: true,
                minWidth: 200,
            },
            {
                headerName: content.LIST_MODEL_API_CONTENT.activities_table_headers.activity_type,
                field: "type",
                sortable: true,
                filter: true,
                resizable: true,
                minWidth: 150,
            },
            {
                headerName: content.LIST_MODEL_API_CONTENT.activities_table_headers.activity_state,
                field: "state",
                sortable: true,
                filter: true,
                resizable: true,
                minWidth: 150,
            },
            {
                headerName: content.LIST_MODEL_API_CONTENT.activities_table_headers.workspace,
                field: "workspace",
                sortable: true,
                filter: true,
                resizable: true,
                minWidth: 200,
                cellRenderer: ({ value }) => userData?.workspacesMap?.[value] || value || "NA",
                filterValueGetter: (params) => {
                    const val = userData?.workspacesMap?.[params.data.workspace] || params.data.workspace || "NA";
                    return val ? val.toString().toLowerCase() : '';
                },
            },
            {
                headerName: content.LIST_MODEL_API_CONTENT.activities_table_headers.activity_priority,
                field: "priority",
                sortable: true,
                filter: true,
                resizable: true,
                minWidth: 150,
            },
            {
                headerName: content.LIST_MODEL_API_CONTENT.activities_table_headers.modified_at,
                field: "modifiedAt",
                sortable: true,
                filter: true,
                resizable: true,
                minWidth: 200,
                cellRenderer: ({ value }) => formatDate(value),
            },
        ],
        [content],
    )

    // Feature columns for step 2
    const featureColumns = useMemo(() => {
        return [
            {
                headerName: "",
                field: "checkboxSelection",
                headerCheckboxSelection: true,
                checkboxSelection: true,
                width: 50,
                minWidth: 50,
            },
            {
                headerName: content.LIST_MODEL_API_CONTENT.table_headers.name,
                field: "externalName",
                sortable: true,
                filter: true,
                resizable: true,
                minWidth: 200,
            },
            {
                headerName: content.LIST_MODEL_API_CONTENT.table_headers.internal_name,
                field: "internalName",
                sortable: true,
                filter: true,
                resizable: true,
                minWidth: 200,
            },
            {
                headerName: content.LIST_MODEL_API_CONTENT.table_headers.type,
                field: "type",
                sortable: true,
                filter: true,
                resizable: true,
                minWidth: 200,
            },
            {
                headerName: content.LIST_MODEL_API_CONTENT.table_headers.status,
                field: "blocked",
                sortable: true,
                filter: true,
                resizable: true,
                minWidth: 120,
                cellRenderer: (params) => (
                    <span
                        className={`px-2 py-1 rounded-full text-xs ${params.value ? "bg-red-100 text-red-800" : "bg-green-100 text-green-800"}`}
                    >
                        {params.value ? "Blocked" : "Active"}
                    </span>
                ),
            },
        ]
    }, [content])

    // Global feature columns
    const globalFeatureColumns = useMemo(() => [
        {
            headerName: "",
            field: "checkboxSelection",
            headerCheckboxSelection: true,
            checkboxSelection: true,
            width: 50,
            minWidth: 50,
        },
        {
            headerName: content.GLOBAL_FEATURES_CONTENT.table_headers.name,
            field: "externalName",
            sortable: true,
            filter: true,
            resizable: true,
            minWidth: 200,
        },
        {
            headerName: content.GLOBAL_FEATURES_CONTENT.table_headers.status,
            field: "blocked",
            sortable: true,
            filter: true,
            resizable: true,
            minWidth: 120,
            cellRenderer: (params) => (
                <span className={`px-2 py-1 rounded-full text-xs ${params.value ? "bg-red-100 text-red-800" : "bg-green-100 text-green-800"}`}>
                    {params.value ? "Blocked" : "Active"}
                </span>
            )
        }
    ], []);

    // Auto size strategy for both grids
    const activitiesAutoSizeStrategy = useMemo(() => {
        return {
            type: window.innerWidth < 1024 ? "fitCellContents" : "fitGridWidth",
            defaultMinWidth: 150,
            columnLimits: activityColumns.map((col) => ({
                colId: col.field,
                minWidth: col.minWidth || 100,
            })),
        }
    }, [activityColumns])

    const featuresAutoSizeStrategy = useMemo(() => {
        return {
            type: window.innerWidth < 1024 ? "fitCellContents" : "fitGridWidth",
            defaultMinWidth: 150,
            columnLimits: featureColumns.map((col) => ({
                colId: col.field,
                minWidth: col.minWidth || 100,
            })),
        }
    }, [featureColumns])

    const globalAutoSizeStrategy = useMemo(() => ({
        type: window.innerWidth < 1024 ? "fitCellContents" : "fitGridWidth",
        defaultMinWidth: 150,
        columnLimits: globalFeatureColumns.map((col) => ({
            colId: col.field,
            minWidth: col.minWidth || 100,
        })),
    }), [globalFeatureColumns])

    // Fetch campaign features data for step 2
    const fetchCampaignData = async (tenantName, campaignId) => {
        if (!tenantName || !campaignId) return

        setIsLoading(true)
        setError(null)

        try {
            const token = userData?.token
            const organizationId = userData?.selectedOrganization?.org_token
            const role = userData?.role

            const result = await getManageAdobeTargetModalApi(token, organizationId, role, tenantName, campaignId)
            setCampaignData(result.data)
            setFeatures(result.data.features || [])
            return result
        } catch (err) {
            console.error("Error fetching campaign data:", err)
            setCampaignData(null)
            setFeatures([])
            setError(err?.message || "Failed to fetch campaign data")
        } finally {
            setIsLoading(false)
        }
    }

    // Helper to map blockedFeatures array to table row objects
    const mapGlobalFeaturesToRows = (blockedFeatures = []) => {
        return (blockedFeatures || []).map((internalName) => ({
            internalName,
            externalName: internalName, // If you have a mapping for display name, use it here
            type: "-", // If you have type info, map it here
            blocked: true,
        }));
    };

    const fetchGlobalFeatures = async (fetchOnly = false) => {
        setGlobalLoading(true)
        setGlobalError(null)
        try {
            const token = userData?.token
            const organizationId = userData?.selectedOrganization?.org_token
            const tenantName = tenant_name || userData?.defaultAdobeIO?.tenant_name
            const result = await getGlobalBlocklistForCampaign(token, organizationId, tenantName)
            // result.data: { blockedFeatures: [], blockedFeatureSources: [] }
            if (fetchOnly) return result.data
            setGlobalFeatures(mapGlobalFeaturesToRows(result.data?.blockedFeatures))
        } catch (err) {
            setGlobalError(err?.message || "Failed to fetch global features")
            setGlobalFeatures([])
        } finally {
            setGlobalLoading(false)
        }
    }

    const handleGlobalUnblockFeatures = async () => {
        if (!selectedGlobalRows.length) return
        setGlobalLoading(true)
        try {
            const token = userData?.token
            const organizationId = userData?.selectedOrganization?.org_token
            const tenantName = tenant_name || userData?.defaultAdobeIO?.tenant_name
            // Remove selected from blockedFeatures
            const toUnblock = selectedGlobalRows.map(f => f.internalName)
            const remainingBlocked = globalFeatures
                .filter(f => !toUnblock.includes(f.internalName))
                .map(f => f.internalName)
            await blockOrUnblockGlobalFeatures(token, organizationId, tenantName, remainingBlocked, [])
            await fetchGlobalFeatures()
            setSelectedGlobalRows([])
        } catch (err) {
            setGlobalError(err?.message || "Failed to unblock features globally")
        } finally {
            setGlobalLoading(false)
        }
    }

    // When leaving global blocklist section, clear selection/search state
    useEffect(() => {
        if (!showGlobalFeatures) {
            setSelectedGlobalRows([])
            setGlobalSearchText("")
            setGlobalError(null)
            setGlobalLoading(false)
        }
    }, [showGlobalFeatures])

    // Initial data loading
    useEffect(() => {
        const tenantName = tenant_name || userData?.defaultAdobeIO?.tenant_name

        if (tenantName) {
            // If campaign_id is provided, go directly to step 2
            if (campaign_id) {
                setCampaignId(campaign_id)
                fetchCampaignData(tenantName, campaign_id)
                setCurrentStep(2)
                setShowBackNavigationArrow(false)
            }
        }
    }, [userData])

    // Handle activity click to navigate to step 2
    const handleActivityClick = (activity) => {
        const tenantName = tenant_name || userData?.defaultAdobeIO?.tenant_name
        if (!tenantName || !activity) return

        setCampaignId(activity.id)
        setCampaignName(activity.name)
        fetchCampaignData(tenantName, activity.id)
        setCurrentStep(2)
        setShowBackNavigationArrow(false)
    }

    // Handle back button click to return to step 1
    const handleBackToActivities = () => {
        setCurrentStep(1)
        setCampaignId("")
        setCampaignName("")
        setFeatures([])
        setCampaignData(null)
        setSelectedRows([])
        setError(null)
        setShowBackNavigationArrow(true)
        setShowGlobalFeatures(false)
        setActivitiesSearchText("")
        setSearchText("")
    }

    // Step 1: Activities search handling
    const handleActivitiesSearch = (e) => {
        setActivitiesSearchText(e.target.value)
        activitiesGridApi?.setQuickFilter(e.target.value)
    }

    const onActivitiesGridReady = (params) => {
        setActivitiesGridApi(params.api)
    }

    // Step 2: Features search and management
    const handleSearch = (e) => {
        setSearchText(e.target.value)
        gridApi?.setQuickFilter(e.target.value)
    }

    const getFilteredData = () => {
        if (!gridApi) return []
        const filteredData = []
        gridApi.forEachNodeAfterFilterAndSort((node) => filteredData.push(node.data))
        return filteredData
    }

    const onGridReady = (params) => {
        setGridApi(params.api)
    }

    const onSelectionChanged = () => {
        if (!gridApi) return
        const selectedNodes = gridApi.getSelectedNodes()
        const selectedData = selectedNodes.map((node) => node.data)
        setSelectedRows(selectedData)
    }

    const onGlobalGridReady = (params) => {
        setGlobalGridApi(params.api)
    }

    const onGlobalSelectionChanged = () => {
        if (!globalGridApi) return
        const selectedNodes = globalGridApi.getSelectedNodes()
        const selectedData = selectedNodes.map((node) => node.data)
        setSelectedGlobalRows(selectedData)
    }

    const handleGlobalSearch = (e) => {
        setGlobalSearchText(e.target.value)
        globalGridApi?.setQuickFilter(e.target.value)
    }

    const updateBlocklist = async (blockedFeatures, blockedFeatureSources = [], isGlobal = false) => {
        try {
            const tenantName = userData?.defaultAdobeIO?.tenant_name

            const result = await blockFeaturesForCampaign(
                userData?.token,
                userData?.selectedOrganization?.org_token,
                tenantName,
                campaignId,
                blockedFeatures,
                blockedFeatureSources,
                isGlobal
            )

            if (result.status) {
                await fetchCampaignData(tenantName, campaignId)
            }
        } catch (error) {
            console.log(error)
        }
    }

    const handleBlockFeatures = async () => {
        if (selectedRows.length === 0 || !campaignId) return
        setIsBlocking(true)
        try {
            // Get current blocklist
            const currentBlockedFeatures = features.filter((f) => f.blocked).map((f) => f.internalName)

            // Add newly selected features to block
            const featuresToBlock = selectedRows.map((row) => row.internalName)
            const allBlockedFeatures = [...new Set([...currentBlockedFeatures, ...featuresToBlock])]
            await updateBlocklist(allBlockedFeatures, [])

            // Clear selection
            gridApi?.deselectAll()
            setSelectedRows([])
        } catch (err) {
            console.error("Error blocking features:", err)
            setError(err?.message || "Failed to block features")
        } finally {
            setIsBlocking(false)
        }
    }

    const handleBlockFeaturesGlobally = async () => {
        if (selectedRows.length === 0 || !campaignId) return
        setIsBlocking(true)
        try {
            const globalFeatures = await fetchGlobalFeatures(true)
            // Get current blocklist
            const currentBlockedFeatures = features.filter((f) => f.blocked).map((f) => f.internalName)

            // Add newly selected features to block
            const featuresToBlock = selectedRows.map((row) => row.internalName)
            const allBlockedFeatures = [...new Set([...globalFeatures?.blockedFeatures, ...featuresToBlock])]
            await updateBlocklist(allBlockedFeatures, [], true)

            // Clear selection
            gridApi?.deselectAll()
            setSelectedRows([])
        } catch (err) {
            console.error("Error blocking features:", err)
            setError(err?.message || "Failed to block features")
        } finally {
            setIsBlocking(false)
        }
    }

    const handleUnblockFeatures = async () => {
        if (selectedRows.length === 0 || !campaignId) return
        setIsBlocking(true)
        try {
            // Get current blocklist
            const currentBlockedFeatures = features.filter((f) => f.blocked).map((f) => f.internalName)

            // Remove selected features from blocklist
            const featuresToUnblock = selectedRows.map((row) => row.internalName)
            const remainingBlockedFeatures = currentBlockedFeatures.filter((feature) => !featuresToUnblock.includes(feature))

            await updateBlocklist(remainingBlockedFeatures, [])

            // Clear selection
            gridApi?.deselectAll()
            setSelectedRows([])
        } catch (err) {
            console.error("Error unblocking features:", err)
            setError(err?.message || "Failed to unblock features")
        } finally {
            setIsBlocking(false)
        }
    }
    return (
        <>
            {isBlocking && <Loader />}

            {/* Back to Activities Button - Top Level */}
            {(showGlobalFeatures || (currentStep === 2 && !campaign_id)) && (
                <div>
                    <button
                        onClick={handleBackToActivities}
                        className="font-medium text-primary hover:underline flex items-center"
                    >
                        <img src={backwardArrow} alt='' className='w-5 h-5 mr-2' />
                        {content.LIST_MODEL_API_CONTENT.back_to_activities}
                    </button>
                </div>
            )}

            {/* Title and Description */}
            <div className="flex flex-col md:flex-row w-full justify-between items-start md:items-center gap-4">
                <h1 className="text-xl font-medium leading-tight tracking-tight text-gray-900 md:text-2xl">
                    {content.LIST_MODEL_API_CONTENT.title}
                </h1>
                {currentStep !== 2 && (
                    !showGlobalFeatures && (
                        <button
                            onClick={() => { setShowGlobalFeatures(true); fetchGlobalFeatures(); setShowBackNavigationArrow(false); }}
                            className="font-medium text-primary hover:underline"
                        >
                            {content.LIST_MODEL_API_CONTENT.manage_global_features}
                        </button>
                    )
                )}
            </div>
            <p className="text-gray-600 mt-2 mb-4">{content.LIST_MODEL_API_CONTENT.description}</p>

            {showGlobalFeatures ? (
                <>
                    <h2 className="text-lg font-semibold mb-2">{content.GLOBAL_FEATURES_CONTENT.title}</h2>
                    {globalError && <div className="mb-4 py-2 px-4 bg-red-50 text-red-800 rounded border border-red-200">{globalError}</div>}
                    <AgTableCard
                        className="mb-8 w-full"
                        header={
                            <div className="flex flex-col md:flex-row gap-4 items-start md:items-center w-full">
                                <div className="w-full sm:w-80">
                                    <Input
                                        value={globalSearchText}
                                        onChange={handleGlobalSearch}
                                        prefixIcon={<Search className="w-4 h-4 text-stride-muted" />}
                                        placeholder={content.GLOBAL_FEATURES_CONTENT.search_placeholder}
                                        clearable
                                    />
                                </div>
                                {selectedGlobalRows.length > 0 && (
                                    <PrimaryButton 
                                        handleClick={handleGlobalUnblockFeatures} 
                                        disabled={globalLoading}
                                        className="w-full md:w-auto"
                                    >
                                        Unblock Selected Globally ({selectedGlobalRows.length})
                                    </PrimaryButton>
                                )}
                            </div>
                        }
                        tableId="manage-adobe-target-global-features-table"
                        loading={globalLoading}
                        emptyMessage={content.GLOBAL_FEATURES_CONTENT.no_global_features}
                        rowData={globalFeatures}
                        columnDefs={globalFeatureColumns}
                        defaultColDef={{ sortable: true, filter: true, resizable: true }}
                        rowSelection="multiple"
                        onSelectionChanged={onGlobalSelectionChanged}
                        autoSizeStrategy={globalAutoSizeStrategy}
                        onGridReady={onGlobalGridReady}
                    />
                </>
            ) : (
                <>
                    {/* Step 1: Activities List */}
                    {currentStep === 1 && (
                        <AgTableCard
                            header={
                                <div className="w-full sm:w-80">
                                    <Input
                                        value={activitiesSearchText}
                                        onChange={handleActivitiesSearch}
                                        prefixIcon={<Search className="w-4 h-4 text-stride-muted" />}
                                        placeholder={content.LIST_MODEL_API_CONTENT.activity_search_placeholder}
                                        clearable
                                    />
                                </div>
                            }
                            className="w-full"
                            tableId="manage-adobe-target-model-api-table"
                            loading={initialLoading}
                            emptyMessage={content.LIST_MODEL_API_CONTENT.no_data_found_msg}
                            rowData={data || []}
                            columnDefs={activityColumns}
                            defaultColDef={{
                                sortable: true,
                                filter: true,
                                resizable: true,
                            }}
                            autoSizeStrategy={activitiesAutoSizeStrategy}
                            onGridReady={onActivitiesGridReady}
                        />
                    )}

                    {/* Step 2: Features Management */}
                    {currentStep === 2 && (
                        <>
                            <div className="flex flex-col md:flex-row items-start md:items-center gap-4 mb-4">
                                {/* Campaign ID Display Card */}
                                <div className="bg-white border border-gray-200 rounded-lg py-[6px] px-2 shadow-sm w-full md:w-auto">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center space-x-4">
                                            <div className="flex items-center space-x-2">
                                                <div className="w-2 h-2 bg-primary rounded-full"></div>
                                                <span className="text-sm font-medium text-gray-600">
                                                    {content.LIST_MODEL_API_CONTENT.activity_id_label}
                                                </span>
                                            </div>
                                            <div className="flex items-center space-x-2">
                                                <span className="text-lg font-semibold text-gray-900">{campaignId}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Campaign ID Display name */}
                                <div className="bg-white border border-gray-200 rounded-lg py-[6px] px-2 shadow-sm w-full md:w-auto">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center space-x-4">
                                            <div className="flex items-center space-x-2">
                                                <div className="w-2 h-2 bg-primary rounded-full"></div>
                                                <span className="text-sm font-medium text-gray-600">
                                                    {content.LIST_MODEL_API_CONTENT.activity_name_label}
                                                </span>
                                            </div>
                                            <div className="flex items-center space-x-2">
                                                <span className="text-lg font-semibold text-gray-900">{campaignName}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {error && <div className="mb-4 px-4 py-2 bg-red-50 text-red-800 rounded-lg border border-red-200">{error}</div>}

                            <AgTableCard
                                header={
                                    <div className="flex flex-col md:flex-row justify-between w-full items-start md:items-center gap-4">
                                        <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center w-full md:w-auto">
                                            <div className="w-full sm:w-80">
                                                <Input
                                                    value={searchText}
                                                    onChange={handleSearch}
                                                    prefixIcon={<Search className="w-4 h-4 text-stride-muted" />}
                                                    placeholder={content.LIST_MODEL_API_CONTENT.search_placeholder}
                                                    clearable
                                                />
                                            </div>
                                            {selectedRows.length > 0 && (
                                                <div className="flex flex-col sm:flex-row gap-2 w-full md:w-auto">
                                                    {!selectedRows.every(row => row.blocked) && (
                                                        <SecondaryButton 
                                                            handleClick={handleBlockFeatures} 
                                                            disabled={isLoading}
                                                            className="w-full sm:w-auto"
                                                        >
                                                            {content.LIST_MODEL_API_CONTENT.block_selected} ({selectedRows.filter(row => !row.blocked).length})
                                                        </SecondaryButton>
                                                    )}
                                                    {!selectedRows.every(row => row.blocked) && (
                                                        <SecondaryButton 
                                                            handleClick={handleBlockFeaturesGlobally} 
                                                            disabled={isLoading}
                                                            className="w-full sm:w-auto"
                                                        >
                                                            {content.LIST_MODEL_API_CONTENT.block_globally} ({selectedRows.filter(row => !row.blocked).length})
                                                        </SecondaryButton>
                                                    )}
                                                    {!selectedRows.every(row => !row.blocked) && (
                                                        <PrimaryButton 
                                                            handleClick={handleUnblockFeatures} 
                                                            disabled={isLoading}
                                                            className="w-full sm:w-auto"
                                                        >
                                                            {content.LIST_MODEL_API_CONTENT.unblock_selected} ({selectedRows.filter(row => row.blocked).length})
                                                        </PrimaryButton>
                                                    )}
                                                </div>
                                            )}
                                        </div>

                                        <div className="relative flex flex-col md:flex-row gap-3 w-full md:w-auto">
                                            {checkPermission(permission.FUNCTIONS_ONE_OFF_TASK_DOWNLOAD) && features.length > 0 && (
                                                <SecondaryButton 
                                                    handleClick={() => setDownloadDropdown(!downloadDropdown)}
                                                    className="w-full md:w-auto"
                                                >
                                                    {content.LIST_MODEL_API_CONTENT.download_btn}
                                                    <svg
                                                        className="w-2.5 h-2.5 ms-3 ml-2 inline-block"
                                                        aria-hidden="true"
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        fill="none"
                                                        viewBox="0 0 10 6"
                                                    >
                                                        <path
                                                            stroke="currentColor"
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth="2"
                                                            d="m1 1 4 4 4-4"
                                                        />
                                                    </svg>
                                                </SecondaryButton>
                                            )}
                                            {downloadDropdown && (
                                                <div className="z-10 bg-white divide-y divide-gray-100 rounded-lg shadow w-44 absolute top-full right-0">
                                                    <ul className="py-2 text-sm text-gray-700">
                                                        <li onClick={() => setDownloadDropdown(false)}>
                                                            <CSVLink
                                                                data={features}
                                                                filename={`target-features-${campaignId}-${new Date().getTime()}`}
                                                                className="block px-4 py-2 hover:bg-gray-100"
                                                            >
                                                                {content.LIST_MODEL_API_CONTENT.all}
                                                            </CSVLink>
                                                        </li>
                                                        <li onClick={() => setDownloadDropdown(false)}>
                                                            <CSVLink
                                                                data={getFilteredData()}
                                                                filename={`target-features-${campaignId}-${new Date().getTime()}`}
                                                                className="block px-4 py-2 hover:bg-gray-100"
                                                            >
                                                                {content.LIST_MODEL_API_CONTENT.filtered}
                                                            </CSVLink>
                                                        </li>
                                                    </ul>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                }
                                tableId="manage-adobe-target-model-api-features-table"
                                loading={isLoading}
                                emptyMessage={campaignId ? content.LIST_MODEL_API_CONTENT.no_feature_text : content.LIST_MODEL_API_CONTENT.enter_campaign_text}
                                rowData={features}
                                columnDefs={featureColumns}
                                defaultColDef={{
                                    sortable: true,
                                    filter: true,
                                    resizable: true,
                                }}
                                rowSelection="multiple"
                                className="w-full"
                                onSelectionChanged={onSelectionChanged}
                                autoSizeStrategy={featuresAutoSizeStrategy}
                                onGridReady={onGridReady}
                            />
                        </>
                    )}
                </>
            )}
        </>
    )
}

export default ManageAdobeTargetModelApi
